<template>
  <div 
    class="tile" 
    :style="{ backgroundColor }"
    :class="{
      'edit-mode-active': currentMode === 'edit' && isMobile && !isEmpty,
      'sort-mode-active': currentMode === 'sort' && isMobile
    }"
    :data-is-mobile="isMobile"
    @click.stop.prevent="handleClick"
    ref="tileElement"
  >
    <div class="tile-content">
      <img 
        :src="displayIconUrl" 
        alt="" 
        class="tile-icon"
        :class="{
          'default-icon': displayIconUrl === '/bookmark.svg',
          'custom-icon': hasCustomIcon
        }"
        @error="handleIconError"
      >
      <div 
        class="tile-name"
        :class="{'empty-tile-name': isEmpty}"
      >{{ displayName }}</div>
    </div>
    
    <!-- メニューボタン - PCかつ非空タイルでのみ表示 -->
    <div 
      class="tile-menu-button" 
      @click.stop="toggleMenu"
      v-if="shouldShowMenuButton"
    >&#8942;</div>
    
    <!-- ドロップダウンメニュー - PC用 -->
    <div 
      class="tile-dropdown-menu" 
      v-if="shouldShowDropdownMenu"
      @click.stop
    >
      <div class="tile-menu-item" @click.stop="handleEdit">編集</div>
      <div class="tile-menu-item" @click.stop="handleDelete">削除</div>
    </div>
    
    <!-- モバイル用メニュー - 長押し時に表示 -->
    <div 
      class="tile-mobile-menu" 
      v-if="isMobileMenuOpen && isMobile"
      @click.stop
      @touchstart.stop
      @touchend.stop
    >
      <div 
        class="mobile-menu-item" 
        @click.stop="handleEdit"
        @touchstart.stop.prevent
        @touchend.stop.prevent="handleEdit"
      >編集</div>
      <div 
        class="mobile-menu-item" 
        @click.stop="handleDelete"
        @touchstart.stop.prevent
        @touchend.stop.prevent="handleDelete"
      >削除</div>
    </div>
    <!-- ネイティブalert代替トースト -->
    <v-snackbar v-model="snack.show" :color="snack.color" :timeout="4000" location="bottom">
      {{ snack.text }}
    </v-snackbar>
  </div>
</template>

<script>
import logger from '../services/logger';

export default {
  name: 'BookmarkTile',
  props: {
    id: {
      type: String,
      required: true,
      validator: (value) => {
        // 型安全なバリデーション
        const idString = value != null ? String(value) : '';
        if (!value || typeof value !== 'string' || idString.trim() === '') {
          logger.error('[TileFixed] IDプロパティが空または不正な型です:', {
            value,
            type: typeof value,
            converted: idString
          });
          return false;
        }
        return true;
      }
    },
    name: {
      type: String,
      default: '追加'
    },
    iconUrl: {
      type: String,
      default: '/bookmark.svg'
    },
    iconColor: {
      type: String,
      default: '#AAA'
    },
    backgroundColor: {
      type: String,
      default: '#f0f0f0'
    },
    isEmpty: {
      type: Boolean,
      default: true
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    url: {
      type: String,
      default: ''
    },
    currentMode: {
      type: String,
      default: 'normal' // 'normal', 'sort', 'edit'
    },
    customIcon: {
      type: String,
      default: ''
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isMenuOpen: false,
      isMobileMenuOpen: false,
      touchStartTime: 0,
      touchTimer: null,
      lastClickTime: 0,
      isLinkProcessing: false,
      clickCounter: 0,
      // タッチ操作用の変数
      touchStartX: 0,
      touchStartY: 0,
      hasMoved: false,
      // アイコンフォールバック用の変数
      currentIconUrl: this.iconUrl,
      iconFallbackAttempts: 0,
      // ネイティブalert代替トースト
      snack: { show: false, text: '', color: 'error' }
    }
  },
  computed: {
    // タイル名を12文字で制限し、超える場合は省略表示にする
    displayName() {
      if (!this.name || this.name.length <= 12) {
        return this.name;
      }
      return this.name.substring(0, 12) + '...';
    },
    
    // カスタムアイコンがあるかどうか
    hasCustomIcon() {
      return Boolean(this.customIcon && this.customIcon.startsWith('data:image/'));
    },
    
    // 表示するアイコンURL（カスタムアイコン優先）
    displayIconUrl() {
      if (this.hasCustomIcon) {
        return this.customIcon;
      }
      return this.currentIconUrl;
    },
    
    // 【デバッグ】メニューボタンの表示条件
    shouldShowMenuButton() {
      const shouldShow = !this.isMobile && !this.isEmpty && !this.readOnly;
      logger.debug('[TileFixed] Menu button visibility:', {
        id: this.id,
        name: this.name,
        isMobile: this.isMobile,
        isEmpty: this.isEmpty,
        readOnly: this.readOnly,
        shouldShow
      });
      return shouldShow;
    },
    
    // 【デバッグ】ドロップダウンメニューの表示条件
    shouldShowDropdownMenu() {
      const shouldShow = this.isMenuOpen && !this.isMobile && !this.isEmpty;
      logger.debug('[TileFixed] Dropdown menu visibility:', {
        id: this.id,
        isMenuOpen: this.isMenuOpen,
        isMobile: this.isMobile,
        isEmpty: this.isEmpty,
        shouldShow
      });
      return shouldShow;
    }
  },
  watch: {
    // iconUrlプロパティが変更されたときにcurrentIconUrlをリセット
    iconUrl(newUrl) {
      this.currentIconUrl = newUrl;
      this.iconFallbackAttempts = 0;
    }
  },
  mounted() {
    // 【デグレ防止】必須プロパティの検証
    this.validateRequiredProps();
    
    document.addEventListener('click', this.handleOutsideClick);
    
    // モバイルデバイス用のタッチイベントリスナーを手動で追加（スクロール優先）
    if (this.$refs.tileElement && this.isMobile) {
      this.$refs.tileElement.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.$refs.tileElement.addEventListener('touchmove', this.handleTouchMove, { passive: true });
      this.$refs.tileElement.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      
      logger.debug('[TileFixed] モバイル環境でタッチイベントリスナーを追加しました（スクロール優先）');
    } else if (this.$refs.tileElement) {
      logger.debug('[TileFixed] PC環境のためタッチイベントリスナーをスキップしました');
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    
    // タッチイベントリスナーを削除（モバイル環境のみ）
    if (this.$refs.tileElement && this.isMobile) {
      this.$refs.tileElement.removeEventListener('touchstart', this.handleTouchStart);
      this.$refs.tileElement.removeEventListener('touchmove', this.handleTouchMove);
      this.$refs.tileElement.removeEventListener('touchend', this.handleTouchEnd);
    }
    
    this.clearTouchTimer();
  },
  methods: {
    // 【デグレ防止】必須プロパティの検証メソッド
    validateRequiredProps() {
      const errors = [];
      
      // IDプロパティを安全に文字列化
      const idString = this.id != null ? String(this.id) : '';
      
      // IDプロパティの検証（数値型も許可）
      if (this.id == null || idString.trim() === '') {
        errors.push('IDプロパティが未定義または空です');
      }
      
      // URLとnameが設定されているのにIDがない場合（特に注意が必要）
      if (!this.isEmpty && (this.id == null || idString.trim() === '')) {
        errors.push('非空タイルにIDが設定されていません（削除機能が動作しません）');
      }
      
      if (errors.length > 0) {
        logger.error('[TileFixed] プロパティ検証エラー:', {
          errors,
          props: {
            id: this.id,
            name: this.name,
            url: this.url,
            isEmpty: this.isEmpty
          }
        });
        
        // 開発環境では警告を表示
        if (process.env.NODE_ENV === 'development') {
          logger.warn('[TileFixed] デバッグ情報: タイルコンポーネントのプロパティに問題があります。削除機能などが正常に動作しない可能性があります。');
        }
      } else {
        logger.debug('[TileFixed] プロパティ検証OK:', { id: this.id, name: this.name });
      }
    },
    
    // アイコン読み込みエラー時のフォールバック処理
    handleIconError() {
      logger.debug(`[TileFixed] アイコンエラー検出: ${this.currentIconUrl}`);
      
      // 既にデフォルトアイコンの場合やemptyタイルの場合は何もしない
      if (this.currentIconUrl === '/bookmark.svg' || this.isEmpty || !this.url) {
        logger.debug(`[TileFixed] フォールバック処理をスキップ（デフォルトアイコンまたは空タイル）`);
        return;
      }
      
      // 即座にデフォルトアイコンに切り替え（サービス側で事前検証済みのため）
      logger.debug(`[TileFixed] デフォルトアイコンに切り替え`);
      this.currentIconUrl = '/bookmark.svg';
    },
    
    showSnack(text, color = 'error') {
      this.snack.text = text;
      this.snack.color = color;
      this.snack.show = true;
    },

    toggleMenu(event) {
      logger.debug('[TileFixed] toggleMenu called:', {
        currentMenuState: this.isMenuOpen,
        event: event.type,
        id: this.id,
        name: this.name
      });
      
      event.stopPropagation();
      this.isMenuOpen = !this.isMenuOpen;
      
      logger.debug('[TileFixed] Menu state changed to:', this.isMenuOpen);
    },
    
    // 編集メニュー項目のクリックハンドラ
    handleEdit() {
      this.$emit('edit');
      this.isMenuOpen = false;
    },
    
    // 削除メニュー項目のクリックハンドラ
    handleDelete() {
      // 【デグレ防止】削除前の必須データ検証（数値型IDも許可）
      const idString = this.id != null ? String(this.id).trim() : '';
      if (!idString) {
        logger.error('[TileFixed] 削除失敗: IDが設定されていません', {
          id: this.id,
          idType: typeof this.id,
          name: this.name,
          url: this.url
        });
        this.showSnack('エラー: タイルIDが設定されていないため削除できません。開発者に報告してください。');
        return;
      }
      
      // 削除イベントを引数なしで発行（App.vueで element を使用するため）
      this.$emit('delete');
      this.isMenuOpen = false;
      this.isMobileMenuOpen = false;
      
      // 長押しアクティブ状態のクラスを削除
      if (this.$refs.tileElement) {
        this.$refs.tileElement.classList.remove('long-press-active');
      }
    },
    
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target) && this.isMenuOpen) {
        this.isMenuOpen = false;
      }
    },
    
    // クリックハンドラ - 親コンポーネントにイベント委任
    handleClick(event) {
      const eventType = event ? event.type : 'unknown';
      const eventTarget = event && event.target ? event.target.tagName : 'unknown';
      
      this.clickCounter += 1;
      const clickId = this.clickCounter;
      const timestamp = Date.now();
      
      logger.debug(`[TileFixed] タイルクリック #${clickId} (${timestamp}ms):`, {
        url: this.url,
        name: this.name, 
        isEmpty: this.isEmpty,
        eventType,
        eventTarget,
        isMobile: this.isMobile,
        currentMode: this.currentMode,
        isLinkProcessing: this.isLinkProcessing
      });
      
      // イベントの伝播を停止
      if (event) {
        event.stopPropagation();
        event.preventDefault();
        logger.debug(`[TileFixed] #${clickId} イベント伝播を停止しました`);
      }
      
      // モバイルの場合：モードに応じた処理
      if (this.isMobile) {
        // 並び替えモードの場合はタッチイベントで処理するためスキップ
        if (this.currentMode === 'sort') {
          logger.debug(`[TileFixed] #${clickId} 並び替えモードなのでスキップ`);
          return;
        }
        
        // リンク編集モードの場合はモバイルメニューを表示
        if (this.currentMode === 'edit' && !this.isEmpty) {
          logger.debug(`[TileFixed] #${clickId} リンク編集モード - モバイルメニュー表示`);
          this.isMobileMenuOpen = true;
          return;
        }
        
        // 通常モード・空タイル・編集モードの空タイルの場合は通常処理
        logger.debug(`[TileFixed] #${clickId} モバイル通常処理`);
      }
      
      // 既にリンク処理中の場合はスキップ
      if (this.isLinkProcessing) {
        logger.debug(`[TileFixed] #${clickId} 既に処理中なのでスキップ`);
        return;
      }
      
      // 連続クリック防止
      const now = Date.now();
      if (now - this.lastClickTime < 1000) {
        logger.debug(`[TileFixed] #${clickId} 連続クリックを検出したためスキップ (${now - this.lastClickTime}ms)`);
        return;
      }
      
      this.lastClickTime = now;
      this.isLinkProcessing = true;
      
      try {
        // 空のタイル - 編集モーダル表示
        if (this.isEmpty) {
          logger.debug(`[TileFixed] #${clickId} 空のタイル - 編集イベント発行`);
          this.$emit('click');
        } else if (this.url && this.url !== '#') {
          // 非空のタイルで、URLがある場合は親コンポーネントに処理を委任
          logger.debug(`[TileFixed] #${clickId} 親コンポーネントに処理を委任: ${this.url}`);
          
          const eventData = {
            url: this.url, 
            name: this.name,
            isEmpty: false,
            id: this.id,
            timestamp: now,
            clickId: clickId
          };
          
          this.$emit('click', eventData);
        }
      } catch (error) {
        logger.error(`[TileFixed] #${clickId} クリック処理中にエラー:`, error);
      }
      
      // 1秒後にフラグをリセット
      setTimeout(() => {
        logger.debug(`[TileFixed] #${clickId} リンク処理フラグリセット (${Date.now() - now}ms経過)`);
        this.isLinkProcessing = false;
      }, 1000);
    },
    
    handleTouchStart(event) {
      // メニュー項目がクリックされた場合はここで処理しない
      if (event.target.closest('.mobile-menu-item')) {
        logger.debug('[TileFixed] メニュー項目のタッチを検出 - 処理をスキップ');
        return;
      }
      
      logger.debug(`[TileFixed] タッチスタート検出 (モバイル: ${this.isMobile}, モード: ${this.currentMode})`);
      
      if (!this.isMobile) return;
      
      // 並び替えモードの場合は長押し処理をスキップ（draggableが処理）
      if (this.currentMode === 'sort') {
        logger.debug('[TileFixed] 並び替えモードのためタッチ処理をスキップ');
        return;
      }

      // 読み取り専用モードの場合は長押し処理をスキップ
      if (this.readOnly) {
        logger.debug('[TileFixed] 読み取り専用モードのためタッチ処理をスキップ');
        return;
      }
      
      // スクロール改善：通常モードではデフォルトアクションを妨げない
      
      // タッチ開始位置を記録
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.hasMoved = false;
      this.touchStartTime = Date.now();
      
      logger.debug('[TileFixed] タッチ開始位置記録:', { 
        x: this.touchStartX, 
        y: this.touchStartY,
        time: this.touchStartTime,
        mode: this.currentMode
      });
      
      this.clearTouchTimer();
      
      // リンク編集モードの場合は短いタップでメニュー表示
      if (this.currentMode === 'edit' && !this.isEmpty) {
        this.touchTimer = setTimeout(() => {
          if (!this.hasMoved) {
            logger.debug('[TileFixed] リンク編集モード: モバイルメニューを表示します');
            this.isMobileMenuOpen = true;
            
            if (this.$refs.tileElement) {
              this.$refs.tileElement.classList.add('edit-mode-active');
            }
            
            setTimeout(() => {
              document.addEventListener('touchstart', this.handleOutsideTouchForMenu, { once: true });
            }, 100);
          }
          this.touchStartTime = 0;
        }, 100);
      } else if (this.currentMode === 'normal' && !this.isEmpty) {
        // 通常モードでは長押し時のみメニュー表示（空タイルは除く）
        this.touchTimer = setTimeout(() => {
          if (!this.hasMoved) {
            logger.debug('[TileFixed] 長押し検出: モバイルメニューを表示します');
            this.isMobileMenuOpen = true;
            
            if (this.$refs.tileElement) {
              this.$refs.tileElement.classList.add('long-press-active');
            }
            
            setTimeout(() => {
              document.addEventListener('touchstart', this.handleOutsideTouchForMenu, { once: true });
            }, 100);
          }
          this.touchStartTime = 0;
        }, 700);
      }
    },
    
    handleTouchMove(event) {
      if (!this.isMobile) return;
      
      // メニューが開いている場合は移動処理をスキップ
      if (this.isMobileMenuOpen) return;
      
      const touchX = event.touches[0].clientX;
      const touchY = event.touches[0].clientY;
      
      // 移動距離を計算
      const deltaX = Math.abs(touchX - this.touchStartX);
      const deltaY = Math.abs(touchY - this.touchStartY);
      
      // スクロール改善：しきい値を大きくしてスクロールを優先
      if (deltaX > 15 || deltaY > 15) {
        this.hasMoved = true;
        this.clearTouchTimer();
      }
    },
    
    handleTouchEnd() {
      if (!this.isMobile) return;
      
      // メニューが開いている場合はタッチイベントを無視
      if (this.isMobileMenuOpen) return;
      
      this.clearTouchTimer();
      
      const touchDuration = Date.now() - this.touchStartTime;
      
      // 短いタッチで、ほとんど動いていない場合のみクリックとして処理
      if (touchDuration < 300 && this.touchStartTime > 0 && !this.hasMoved) {
        // モバイルでの短いタップ - クリックイベントを発火
        if (this.isEmpty) {
          this.$emit('click');
        } else if (this.url) {
          // 親コンポーネントにURLを開く処理を委任
          this.$emit('click', {
            url: this.url,
            name: this.name,
            isEmpty: this.isEmpty
          });
        }
      }
      
      this.touchStartTime = 0;
    },
    
    clearTouchTimer() {
      if (this.touchTimer) {
        clearTimeout(this.touchTimer);
        this.touchTimer = null;
      }
    },
    
    // モバイルメニューを閉じる
    closeMobileMenu() {
      this.isMobileMenuOpen = false;
      document.removeEventListener('touchstart', this.handleOutsideTouchForMenu);
    },
    
    // モバイルメニューの外側をタップしたときの処理
    handleOutsideTouchForMenu(event) {
      const menuElement = this.$el.querySelector('.tile-mobile-menu');
      if (menuElement && !menuElement.contains(event.target)) {
        this.isMobileMenuOpen = false;
      }
    }
  }
}
</script>

<style scoped>
/* タイルの基本スタイル */
.tile {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  background-color: #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-sizing: border-box;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.15s ease-out, box-shadow 0.2s ease;
  transform-origin: center bottom;
  will-change: transform;
}

/* モバイル環境特有のタイルスタイル */
.tile[data-is-mobile="true"] {
  /* スクロール改善：通常モードではスクロールを許可 */
  touch-action: manipulation !important;
  -webkit-touch-action: manipulation !important;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  /* ドラッグ操作はモードによって制御 */
  -webkit-user-drag: none;
}

/* 並び替えモード時のみtouch-actionを厳格に制御 */
.tile[data-is-mobile="true"].sort-mode-active {
  touch-action: none !important;
  -webkit-touch-action: none !important;
}

/* PC環境特有のタイルスタイル */
.tile[data-is-mobile="false"] {
  /* PCではドラッグ操作を許可 */
  touch-action: auto !important;
  -webkit-touch-action: auto !important;
}

/* モバイルメニュー内の項目はタップ可能にする */
.tile-mobile-menu,
.tile-mobile-menu .mobile-menu-item {
  touch-action: auto !important;
  -webkit-touch-action: auto !important;
  pointer-events: auto !important;
  cursor: pointer !important;
  user-select: none !important;
  -webkit-user-select: none !important;
}

.tile-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 12px 8px;
  box-sizing: border-box;
  gap: 8px; /* アイコンと文字の間隔を統一 */
}

.tile-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
  flex-shrink: 0;
  /* 中央揃えを確実にする */
  display: block;
  margin: 0 auto;
}

.tile-icon.default-icon {
  opacity: 0.6;
  filter: grayscale(0.8) brightness(1.2) contrast(0.5);
}

.tile-name {
  font-size: 11px;
  color: #666;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.2;
  padding: 0 2px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  /* 2行制限: line-clampで省略表示 */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 24px; /* テキストの最小高さを確保 */
}

.empty-tile-name {
  font-style: italic;
  color: #999;
}

/* メニューボタンのスタイル */
.tile-menu-button {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  background-color: rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s ease;
  z-index: 10;
  opacity: 0;
  pointer-events: none;
}

.tile:hover .tile-menu-button {
  opacity: 1;
  pointer-events: auto;
}

.tile-menu-button:hover {
  background-color: rgba(255, 255, 255, 1);
  transform: scale(1.1);
}

/* ドロップダウンメニューのスタイル */
.tile-dropdown-menu {
  position: absolute;
  top: 25px;
  right: 5px;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 20;
  min-width: 80px;
}

.tile-menu-item {
  padding: 8px 12px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s ease;
}

.tile-menu-item:last-child {
  border-bottom: none;
}

.tile-menu-item:hover {
  background-color: #f5f5f5;
}

/* モバイル用メニューのスタイル */
.tile-mobile-menu {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: row;
  min-width: 200px;
  overflow: hidden;
}

.mobile-menu-item {
  flex: 1;
  padding: 16px 20px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  text-align: center;
  border-right: 1px solid #eee;
  transition: background-color 0.2s ease;
  user-select: none;
}

.mobile-menu-item:last-child {
  border-right: none;
}

.mobile-menu-item:hover,
.mobile-menu-item:active {
  background-color: #f0f0f0;
}

/* 長押しアクティブ状態のスタイル */
.tile.long-press-active {
  transform: scale(0.95);
  box-shadow: 0 0 0 2px #007bff;
  transition: all 0.1s ease;
}

/* ホバー効果（ドック拡大アニメーション中はJSがtransformを制御） */
.tile:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* アクティブ状態 */
.tile:active {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 編集モード・並び替えモード時のスタイル（App.vueから継承） */
.tile.edit-mode-active {
  border: 2px solid #4CAF50 !important;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.3) !important;
}

.tile.sort-mode-active {
  border: 2px solid #FF9800 !important;
  box-shadow: 0 0 8px rgba(255, 152, 0, 0.3) !important;
}

/* レスポンシブ対応 */
@media (max-width: 480px) {
  .tile {
    width: 90px;
    height: 90px;
  }

  .tile-content {
    padding: 10px 6px;
    gap: 6px;
  }

  .tile-icon {
    width: 28px;
    height: 28px;
  }

  .tile-name {
    font-size: 10px;
    min-height: 20px;
  }

  .mobile-menu-item {
    padding: 14px 16px;
    font-size: 13px;
  }
}

@media (max-width: 360px) {
  .tile {
    width: 80px;
    height: 80px;
    padding: 6px;
  }

  .tile-content {
    padding: 8px 4px;
    gap: 4px;
  }

  .tile-icon {
    width: 24px;
    height: 24px;
  }

  .tile-name {
    font-size: 9px;
    min-height: 18px;
  }
}
</style>
